/**
 * #5068 — where a drawing's labels go, shared by the room, the TV and the server so all three agree. A label never
 * covers another label and stays on the sheet; within that it avoids the drawing itself, judged against every stroke
 * actually drawn (an occupancy grid), not against bounding boxes, which an arrow's diagonal makes useless.
 *
 * For each labelled part, in drawing order: a container (a box, a circle, a cell, a cloud) takes its label inside when
 * it fits and nothing is drawn there; otherwise spots in rings around the part are tried, nearest first, and the one
 * crossing the least drawing wins. A long label wraps onto two lines. Labels are laid out once over every part, so
 * they keep their places as beats appear.
 */
import type { DrawingPart } from './DrawingSchema';
import type { PlacedShape, Point } from './sketchKit';

export interface DrawingLabelBox { x: number; y: number; width: number; height: number }
export interface DrawingLabelInput {
  id: string;
  label: string;
  box: DrawingLabelBox;
  /** The part's strokes as drawn (sheet coordinates). */
  strokes?: Point[][];
  /** Extra drawn points (3D outlines projected to the sheet), when there are no strokes. */
  points?: Point[];
  /** Text the kit draws on the part (a battery's + and −), kept clear too. */
  texts?: DrawingLabelBox[];
  /** The label may sit inside the part (a box or a circle). */
  container?: boolean;
}
export interface DrawingLabel extends DrawingLabelBox {
  id: string;
  text: string;
  lines: string[];
  fontSize: number;
  /** Where a leader line ends on the part's drawing; at the label's own centre when it sits inside its part. */
  anchor: { x: number; y: number };
  inside: boolean;
}

/** Kit items whose label belongs inside them. */
export const DRAWING_CONTAINER_KITS: ReadonlySet<string> = new Set(['box', 'circle', 'cell', 'cloud', 'block3d']);

const CELL = 6;
/** Handwriting runs about half an em per character. */
const textWidth = (text: string, fontSize: number) => text.length * fontSize * .52 + 6;
const overlaps = (a: DrawingLabelBox, b: DrawingLabelBox, pad = 0) =>
  a.x < b.x + b.width + pad && b.x < a.x + a.width + pad && a.y < b.y + b.height + pad && b.y < a.y + a.height + pad;

/** A label wider than this wraps at the space nearest its middle. */
function wrap(text: string, fontSize: number, maxWidth: number): string[] {
  if (textWidth(text, fontSize) <= maxWidth || !text.includes(' ')) return [text];
  const middle = text.length / 2;
  let split = -1;
  for (let index = 0; index < text.length; index++) if (text[index] === ' ' && (split === -1 || Math.abs(index - middle) < Math.abs(split - middle))) split = index;
  return [text.slice(0, split), text.slice(split + 1)];
}

/** Counts drawn cells in any rectangle in constant time (a summed-area table over the grid). */
class Occupancy {
  private readonly columns: number;
  private readonly rows: number;
  private readonly cells: Uint8Array;
  private sums: Int32Array | null = null;
  constructor(readonly width: number, readonly height: number) {
    this.columns = Math.ceil(width / CELL); this.rows = Math.ceil(height / CELL);
    this.cells = new Uint8Array(this.columns * this.rows);
  }
  private mark(x: number, y: number) {
    const column = Math.floor(x / CELL), row = Math.floor(y / CELL);
    if (column >= 0 && row >= 0 && column < this.columns && row < this.rows) this.cells[row * this.columns + column] = 1;
  }
  line(points: Point[], closed: boolean) {
    const path = closed && points.length > 2 ? [...points, points[0]] : points;
    if (path.length === 1) this.mark(path[0][0], path[0][1]);
    for (let index = 1; index < path.length; index++) {
      const [ax, ay] = path[index - 1], [bx, by] = path[index];
      const steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / 3));
      for (let step = 0; step <= steps; step++) this.mark(ax + (bx - ax) * step / steps, ay + (by - ay) * step / steps);
    }
    this.sums = null;
  }
  rect(box: DrawingLabelBox) {
    for (let y = box.y; y <= box.y + box.height; y += CELL / 2) for (let x = box.x; x <= box.x + box.width; x += CELL / 2) this.mark(x, y);
    this.sums = null;
  }
  /** Drawn cells inside `box`. */
  count(box: DrawingLabelBox): number {
    if (!this.sums) {
      const sums = new Int32Array((this.columns + 1) * (this.rows + 1)), stride = this.columns + 1;
      for (let row = 0; row < this.rows; row++) for (let column = 0; column < this.columns; column++)
        sums[(row + 1) * stride + column + 1] = this.cells[row * this.columns + column] + sums[row * stride + column + 1] + sums[(row + 1) * stride + column] - sums[row * stride + column];
      this.sums = sums;
    }
    const stride = this.columns + 1, clamp = (value: number, max: number) => Math.max(0, Math.min(max, value));
    const x0 = clamp(Math.floor(box.x / CELL), this.columns), x1 = clamp(Math.ceil((box.x + box.width) / CELL), this.columns);
    const y0 = clamp(Math.floor(box.y / CELL), this.rows), y1 = clamp(Math.ceil((box.y + box.height) / CELL), this.rows);
    const sums = this.sums;
    return sums[y1 * stride + x1] - sums[y0 * stride + x1] - sums[y1 * stride + x0] + sums[y0 * stride + x0];
  }
}

export function layoutDrawingLabels(parts: DrawingLabelInput[], sheet: { width: number; height: number }, fontSize = 22): DrawingLabel[] {
  const drawn = new Occupancy(sheet.width, sheet.height);
  for (const part of parts) {
    for (const stroke of part.strokes ?? []) drawn.line(stroke, false);
    if (!part.strokes?.length && part.points?.length) for (const point of part.points) drawn.line([point], false);
    if (!part.strokes?.length && !part.points?.length) {
      const b = part.box;
      drawn.line([[b.x, b.y], [b.x + b.width, b.y], [b.x + b.width, b.y + b.height], [b.x, b.y + b.height]], true);
    }
    for (const text of part.texts ?? []) drawn.rect(text);
  }
  const placed: DrawingLabel[] = [];
  const onSheet = (box: DrawingLabelBox) => box.x >= 2 && box.y >= 2 && box.x + box.width <= sheet.width - 2 && box.y + box.height <= sheet.height - 2;
  const free = (box: DrawingLabelBox) => onSheet(box) && !placed.some(label => overlaps(label, box, 4));
  for (const part of parts) {
    const text = part.label.trim();
    if (!text) continue;
    const b = part.box, cx = b.x + b.width / 2, cy = b.y + b.height / 2;
    const lineHeight = fontSize * 1.15;
    let chosen: { box: DrawingLabelBox; lines: string[]; inside: boolean } | null = null;
    // Inside a container, when it fits clear of anything drawn there (its own outline is at the edge).
    if (part.container) {
      for (const lines of [[text], wrap(text, fontSize, Math.max(60, b.width - 16))]) {
        const width = Math.max(...lines.map(line => textWidth(line, fontSize))), height = lines.length * lineHeight;
        const box = { x: cx - width / 2, y: cy - height / 2, width, height };
        if (width <= b.width - 12 && height <= b.height - 6 && free(box) && drawn.count(box) === 0) { chosen = { box, lines, inside: true }; break; }
      }
    }
    if (!chosen) {
      const lines = wrap(text, fontSize, 240);
      const width = Math.max(...lines.map(line => textWidth(line, fontSize))), height = lines.length * lineHeight;
      let best: DrawingLabelBox | null = null, bestScore = Infinity;
      for (const distance of [8, 18, 32, 52, 80, 120, 170]) {
        for (let step = 0; step < 16; step++) {
          const angle = step / 16 * Math.PI * 2, dx = Math.cos(angle), dy = Math.sin(angle);
          // Beside the part along this direction, `distance` clear of its box.
          const box = { x: cx + dx * (b.width / 2 + width / 2 + distance) - width / 2, y: cy + dy * (b.height / 2 + height / 2 + distance) - height / 2, width, height };
          if (!free(box)) continue;
          // Crossing drawing costs far more than walking further out; sides read better than corners.
          const score = drawn.count(box) * 60 + distance + (Math.abs(dx) > .2 && Math.abs(dy) > .2 ? 6 : 0);
          if (score < bestScore) { best = box; bestScore = score; }
        }
        if (best && bestScore < distance + 10) break;
      }
      // Everything nearby is taken: the first free row below the part, then anywhere on the sheet.
      if (!best) {
        for (let y = 2; y + height <= sheet.height - 2 && !best; y += lineHeight / 2) {
          for (let x = 2; x + width <= sheet.width - 2 && !best; x += width / 2) {
            const box = { x, y, width, height };
            if (free(box)) best = box;
          }
        }
      }
      chosen = { box: best ?? { x: 2, y: 2, width, height }, lines, inside: false };
    }
    const box = chosen.box, lx = box.x + box.width / 2, ly = box.y + box.height / 2;
    // The leader ends on the part's own drawing (its nearest point), not an empty corner of its box.
    const points = part.strokes?.length ? part.strokes.flat() : part.points ?? [];
    const nearest = points.reduce<Point | null>((found, point) =>
      !found || Math.hypot(point[0] - lx, point[1] - ly) < Math.hypot(found[0] - lx, found[1] - ly) ? point : found, null);
    const anchor = chosen.inside ? { x: lx, y: ly } : nearest ? { x: nearest[0], y: nearest[1] }
      : { x: Math.min(b.x + b.width, Math.max(b.x, lx)), y: Math.min(b.y + b.height, Math.max(b.y, ly)) };
    placed.push({ ...box, id: part.id, text, lines: chosen.lines, fontSize, anchor, inside: chosen.inside });
    // Later labels keep clear of this one's text and of its leader.
    if (!chosen.inside) drawn.line([[Math.min(box.x + box.width, Math.max(box.x, anchor.x)), Math.min(box.y + box.height, Math.max(box.y, anchor.y))], [anchor.x, anchor.y]], false);
  }
  return placed;
}

/** Label inputs for sketch parts: their strokes as drawn, their kit text, and whether the label may sit inside. */
export function sketchLabelInputs(parts: readonly DrawingPart[], placedOf: (part: DrawingPart) => PlacedShape | null | undefined): DrawingLabelInput[] {
  return parts.flatMap(part => {
    const shape = placedOf(part);
    if (!shape) return [];
    return [{ id: part.id, label: part.label, box: shape.box, container: DRAWING_CONTAINER_KITS.has(part.kit),
      strokes: shape.strokes.map(stroke => stroke.closed && stroke.points.length > 2 ? [...stroke.points, stroke.points[0]] : stroke.points),
      texts: shape.texts.map(text => { const width = text.text.length * text.size * .6; return { x: text.x - width / 2, y: text.y - text.size * .8, width, height: text.size }; }) }];
  });
}
